<template lang="pug">
student-list.student-management(
  ref='template',
  fullNamePlaceholder='氏名',
  statusPlaceholder='ステータス',
  modalTitle='ユーザー登録（生徒）',
  modalPasswordPlaceholder='パスワード',
  modalPasswordConfirmPlaceholder='パスワード',
  :userRole='userRole',
  :currentLink='currentLink',
  :sideMenuLinkEvent='sideMenuLinkEvent',
  :logoutEvent='logoutEvent',
  :breadcrumbs='breadcrumbs',
  :gradeSelectOptions='gradeSelectOptions',
  :searchEvent='searchEvent',
  :qrOutputEvent='qrOutputEvent',
  :downloadEvent='downloadEvent',
  :sort='sort',
  :studentDatas='studentDatas',
  :setEditDataEvent='setEditDataEvent',
  :pagenationStatus='pagenationStatus',
  :pagenationEvent='pagenationEvent',
  :studentEditData='studentEditData',
  :modalGradeSelectOptions='modalGradeSelectOptions',
  :modalKeepButtonEvent='modalKeepButtonEvent',
  :modalErrorMessages='modalErrorMessages',
  @sort-changed='sortChanged'
)
  template(#sideMenuSeparateText) 設定
  template(#logoutText) サインアウト
  template(#searchButtonText) 絞り込む
  template(#qrOutputIconLabel) QRコード
  template(#downloadIconLabel) ダウンロード
  template(#userNameHeader) 氏名
  template(#bcbtCodeHeader) ID
  template(#gradeNameHeader) 学年
  template(#statusNameHeader) ステータス
  template(#editHeader) 編集
  template(#popupEditText) 編集
  template(#modalGradeNameLabel) 学年（編集不可）
  template(#modalUserNameLabel) 氏名（編集不可）
  template(#modalPasswordLabel) パスワード
  template(#modalPasswordLabelAnnotation) ※変更がない場合は空白
  template(#modalPasswordConfirmLabel) パスワード
  template(#modalPasswordConfirmLabelAnnotation) ※変更する場合の再確認用
  template(#modalStatusLabel) ステータス（編集不可）
  template(#modalKeepText) 保存
</template>

<script lang="ts">
import { Component, Mixins, Ref } from 'vue-property-decorator'
import LoginMethods from '@/mixins/LoginMethods'
import GradesApi from '@/mixins/api/GradesApi'
import StudentsApi from '@/mixins/api/StudentsApi'
import StudentList from '@/components/templates/manager/StudentList.vue'
import { breadcrumbType } from '@/models/breadcrumbModel'
import {
  StudentListType,
  StudentListSortType,
  StudentEditType,
  StudentSearchParamsType,
} from '@/models/studentListModel'
import { InputItem } from '@/models/inputItem'
import { PaginationStatus } from '@/models/paginationModel'
import { LinkSetting, SideMenuSettings } from '@/const/SideMenuSettings'
import { UserRoles } from '@/const/UserRoles'
import { InputItemPlaceholderSetting } from '@/const/DisplaySettings'
import { SortSettings } from '@/const/SortSettings'
import { StudySearchUtils } from '@/utils/StudySearchUtils'
import { StudentManagementUtils } from '@/utils/StudentManagementUtils'

/**
 * 先生・講師・生徒管理画面
 */
@Component({
  components: {
    StudentList,
  },
})
export default class StudentManagement extends Mixins(LoginMethods, GradesApi, StudentsApi) {
  // テンプレートへのアクセス
  @Ref() template!: StudentList

  // パンくず
  private breadcrumbs: breadcrumbType[] = [
    {
      label: 'ユーザー管理',
    },
  ]

  // 先生・講師権限
  private userRole: UserRoles = UserRoles.BRANCH

  // サイドメニューの現在リンク
  private currentLink: LinkSetting = SideMenuSettings.BRANCH_MANAGER_USER

  // ソート情報
  private sort: StudentListSortType | null = null

  // 検索用の学年選択オプション
  private gradeSelectOptions: InputItem[] = []

  // 生徒一覧情報
  private studentDatas: StudentListType[] = []

  // ページネーションの状態
  private pagenationStatus: PaginationStatus | null = null

  // モーダルに表示する生徒編集情報
  private studentEditData: StudentEditType | null = null

  // モーダルの学年選択オプション
  private modalGradeSelectOptions: InputItem[] = []

  // モーダルに表示するエラーメッセージ
  private modalErrorMessages: string[] = []

  // 現在の検索パラメーター
  private currentSearchParams: StudentSearchParamsType | null = null

  // 現在のページ
  private get currentPage(): number {
    return this.pagenationStatus?.page || 1
  }

  // 初期表示処理
  private async mounted() {
    this.$loading.start()
    // デフォルトソート値をセットして1ページ目を検索する
    this.setDefaultSort()
    await this.search(1)
    // 学年選択オプションAPI呼び出し
    const gradeApiResponse = await this.getGrades()
    // 検索用の学年選択オプションを設定
    this.gradeSelectOptions = StudySearchUtils.buildGradeInputItems(
      StudySearchUtils.buildPlaceholder(InputItemPlaceholderSetting.MANAGER_GRADE_ALL),
      gradeApiResponse
    )
    // モーダル用の学年選択オプションを設定
    this.modalGradeSelectOptions = StudySearchUtils.buildGradeInputItems(
      StudySearchUtils.buildPlaceholder(InputItemPlaceholderSetting.MANAGER_GRADE),
      gradeApiResponse
    )
    this.$loading.complete()
  }

  // デフォルトソート値をセット
  private setDefaultSort() {
    this.sort = {
      userName: SortSettings.ASC,
      bcbtCode: SortSettings.ASC,
      gradeName: SortSettings.ASC,
      statusName: SortSettings.ASC,
    }
  }

  // 検索ボタン押下イベント
  private async searchEvent() {
    this.$loading.start()
    // 全チェックボックス、ソートを初期化
    this.template.checkedAll = false
    this.setDefaultSort()
    // 1ページ目を検索
    await this.search(1)
    this.$loading.complete()
  }

  // ページ遷移イベント
  private async pagenationEvent(page: number) {
    this.$loading.start()
    // 指定されたページを検索
    await this.search(page)
    this.$loading.complete()
  }

  // ソート変更時イベント
  private async sortChanged(sort: StudentListSortType) {
    this.$loading.start()
    // ソートを更新
    this.sort = sort
    // 現在のページを検索
    await this.search(this.currentPage)
    this.$loading.complete()
  }

  // 検索処理
  private async search(page: number) {
    if (!this.sort) {
      return
    }
    // 生徒一覧検索API呼び出し
    this.currentSearchParams = {
      page: page,
      gradeId: this.template.gradeValue ? Number(this.template.gradeValue) : null,
      userName: this.template.fullName,
      statusName: this.template.status,
      userNameSort: this.sort.userName.code,
      bcbtCodeSort: this.sort.bcbtCode.code,
      gradeSort: this.sort.gradeName.code,
      statusNameSort: this.sort.statusName.code,
    }
    const apiResponse = await this.searchStudents(this.currentSearchParams, this.userRole)
    this.studentDatas = StudentManagementUtils.formatStudentDatas(apiResponse)
    this.pagenationStatus = StudentManagementUtils.getPagenationStatus(apiResponse)
    // チェックボックスのチェック状態をセット
    this.setCheckedTeacherUserIds()
  }

  // チェックボックスのチェック状態をセットする処理
  private setCheckedTeacherUserIds() {
    if (this.template.checkedAll) {
      // 全チェックボックスがチェックされている場合、全データのチェックボックスをチェックする
      this.template.checkedStudentUserIds = this.studentDatas.map((studentData) => {
        return studentData.id
      })
    } else {
      // チェックされていない場合は全てを未チェックにする
      this.template.checkedStudentUserIds = []
    }
  }

  // QRコード出力イベント
  private async qrOutputEvent() {
    if (this.currentSearchParams === null) {
      return
    }
    // 対象チェックボックスにチェックがない場合はダウンロードを行わない
    if (this.template.checkedStudentUserIds.length === 0) {
      alert('ダウンロード対象をチェックしてください')
      return
    }
    // 出力処理
    this.$loading.start()
    // チェックボックスがチェックされた生徒情報を絞り込みに使用する（全選択時は絞り込み不要）
    const studentIds: number[] = this.template.checkedAll ? [] : this.template.checkedStudentUserIds
    try {
      // 直近の検索パラメーターを用いてpdfデータを取得する
      const exportData = await this.downloadStudentQrLoginPdf(this.currentSearchParams, studentIds, this.userRole)
      // aタグを生成し、hrefにデータを設定してclickを実行することでファイルをダウンロード
      const fileLink = document.createElement('a')
      fileLink.href = URL.createObjectURL(exportData)
      fileLink.download = `student_qr_${this.$localDateFns.formatTimestamp(new Date())}.pdf`
      fileLink.click()
      this.$loading.complete()
    } catch {
      alert('エラーが発生しました')
    }
  }

  // ダウンロードイベント
  private async downloadEvent() {
    if (this.currentSearchParams === null) {
      return
    }
    // 対象チェックボックスにチェックがない場合はダウンロードを行わない
    if (this.template.checkedStudentUserIds.length === 0) {
      alert('ダウンロード対象をチェックしてください')
      return
    }
    // ダウンロード処理
    this.$loading.start()
    // チェックボックスがチェックされた生徒情報を絞り込みに使用する（全選択時は絞り込み不要）
    const studentIds: number[] = this.template.checkedAll ? [] : this.template.checkedStudentUserIds
    try {
      // 直近の検索パラメーターを用いてエクセルデータを取得する
      const exportData = await this.exportStudentExcel(this.currentSearchParams, studentIds, this.userRole)
      // aタグを生成し、hrefにデータを設定してclickを実行することでファイルをダウンロード
      const fileLink = document.createElement('a')
      fileLink.href = URL.createObjectURL(exportData)
      fileLink.download = '生徒ユーザ一覧.xlsx'
      fileLink.click()
      this.$loading.complete()
    } catch {
      alert('エラーが発生しました')
    }
  }

  // サイドメニュー遷移イベント
  private sideMenuLinkEvent(link: LinkSetting) {
    // 設定されたリンク先ページへ遷移
    this.$router.push({ name: `Branch${link.name}` })
  }

  // ログアウトイベント
  private logoutEvent() {
    this.logout()
    // ログインページへ遷移
    this.$router.push({ name: 'BranchLogin' })
  }

  private async setEditDataEvent(id: number) {
    // エラーメッセージを初期化する
    this.modalErrorMessages = []
    this.$loading.start()
    // 生徒情報取得API呼び出し
    const apiResponse = await this.getStudentData(id, this.userRole)
    this.studentEditData = StudentManagementUtils.buildBranchStudentEditData(apiResponse)
    this.$loading.complete()
  }

  // モーダルの保存ボタン押下時のイベント
  private async modalKeepButtonEvent() {
    if (!this.studentEditData) {
      return
    }
    // エラーメッセージを初期化する
    this.modalErrorMessages = []
    this.$loading.start()
    try {
      if (this.studentEditData.id) {
        // studentEditDataにIDがある場合は更新処理
        await this.updateStudentData(this.studentEditData, this.userRole)
      }
      // 再検索する
      await this.search(this.currentPage)
      this.$loading.complete()
    } catch (e: any) {
      // エラー発生時はエラーメッセージを設定する
      this.modalErrorMessages = [e.response.data.message]
    }
  }
}
</script>
